import React, { useState, useEffect, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import './SoundTest.css';

// ===== НАСТРОЙКИ S3 =====
const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru';

const SoundTest = ({ ageGroup, onBack, onComplete }) => {
  const questions = [
    {
      id: 1,
      pair: 'Свисток — Дудочка',
      soundUrl: `${S3_BASE_URL}/audio/whistle.mp3`,
      image1: `${S3_BASE_URL}/dudochka.png`,
      image1Label: 'Дудочка',
      image2: `${S3_BASE_URL}/svistok.png`,
      image2Label: 'Свисток',
      correct: 1,
    },
    {
      id: 2,
      pair: 'Колокольчик — Молоточек',
      soundUrl: `${S3_BASE_URL}/audio/bell.mp3`,
      image1: `${S3_BASE_URL}/bell.png`,
      image1Label: 'Колокольчик',
      image2: `${S3_BASE_URL}/molotocheck.png`,
      image2Label: 'Молоточек',
      correct: 0,
    },
    {
      id: 3,
      pair: 'Погремушка — Бубен',
      soundUrl: `${S3_BASE_URL}/audio/rattle.mp3`,
      image1: `${S3_BASE_URL}/buben.png`,
      image1Label: 'Бубен',
      image2: `${S3_BASE_URL}/pogremushka.png`,
      image2Label: 'Погремушка',
      correct: 1,
    },
    {
      id: 4,
      pair: 'Собака — Кошка',
      soundUrl: `${S3_BASE_URL}/audio/cat.mp3`,
      image1: `${S3_BASE_URL}/dog.png`,
      image1Label: 'Собака',
      image2: `${S3_BASE_URL}/cat.png`,
      image2Label: 'Кошка',
      correct: 1,
    },
    {
      id: 5,
      pair: 'Машина — Поезд',
      soundUrl: `${S3_BASE_URL}/audio/train.mp3`,
      image1: `${S3_BASE_URL}/train.png`,
      image1Label: 'Поезд',
      image2: `${S3_BASE_URL}/car.png`,
      image2Label: 'Машина',
      correct: 0,
    },
  ];

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  // Референсы для аудио и таймера
  const audioRef = useRef(null);
  const playTimeoutRef = useRef(null); // НОВОЕ: для очистки таймера

  const totalQuestions = questions.length;
  const isLastQuestion = currentQuestion === totalQuestions - 1;

  useEffect(() => {
    playSound();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current); // НОВОЕ: очистка при размонтировании
      }
    };
  }, [currentQuestion]);

  const playSound = () => {
    const question = questions[currentQuestion];
    if (!question.soundUrl) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // НОВОЕ: очищаем старый таймер, если был
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
    }

    setIsPlaying(true);

    const audio = new Audio(question.soundUrl);
    audioRef.current = audio;

    audio.onended = () => {
      setIsPlaying(false);
      playTimeoutRef.current = null;
    };

    audio.onerror = () => {
      console.error('Не удалось загрузить звук:', question.soundUrl);
      setIsPlaying(false);
      playTimeoutRef.current = null;
    };

    audio.play().catch((err) => {
      if (err.name === 'AbortError') return;
      console.error('Ошибка воспроизведения:', err);
      setIsPlaying(false);
      playTimeoutRef.current = null;
    });

    // НОВОЕ: сохраняем таймер в ref
    playTimeoutRef.current = setTimeout(() => {
      setIsPlaying(false);
    }, 5000);
  };

  const handleSelect = (index) => {
    // Оставляем только защиту от двойного клика по одной и той же картинке
    if (selected !== null) return;

    // НОВОЕ: Если ребенок кликнул, сразу останавливаем звук!
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
    }
    setIsPlaying(false);

    setSelected(index);
    const q = questions[currentQuestion];
    const isCorrect = index === q.correct;
    const newAnswers = [...answers, isCorrect];
    setAnswers(newAnswers);

    const chosenLabel = index === 0 ? q.image1Label : q.image2Label;
    const correctLabel = q.correct === 0 ? q.image1Label : q.image2Label;
    const newLog = [...answerLog, { pair: q.pair, chosenLabel, correctLabel, correct: isCorrect }];
    setAnswerLog(newLog);

    // Если это был последний вопрос — сразу переходим к ритму
    if (isLastQuestion) {
      setTimeout(() => {
        const correctCount = newAnswers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

        const failedPairs = newLog
          .filter(a => !a.correct)
          .map(a => ({ pair: a.pair, chosenLabel: a.chosenLabel, correctLabel: a.correctLabel }));

        if (onComplete) {
          onComplete({ testId: 'SoundTest', score, details: { total: totalQuestions, correct: correctCount, failedPairs } });
        } else {
          onBack();
        }
      }, 800);
      return;
    }

    // Иначе переходим к следующему вопросу
    setTimeout(() => {
      setCurrentQuestion(currentQuestion + 1);
      setSelected(null);
      setImageErrors({});
    }, 800);
  };

  const getStatusColor = (index) => {
    if (index < answers.length) {
      return answers[index] ? 'correct' : 'wrong';
    }
    return 'empty';
  };

  const handleImageError = (questionId, imageKey) => {
    setImageErrors((prev) => ({
      ...prev,
      [`${questionId}-${imageKey}`]: true,
    }));
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current); // НОВОЕ: очистка таймера
    }
    onBack();
  };

  const question = questions[currentQuestion];
  const img1Error = imageErrors[`${question.id}-1`];
  const img2Error = imageErrors[`${question.id}-2`];

  return (
    <div className="sound-test-container">
      <div className="sound-test-card">
        <div className="test-area">
          <div className="images-container">
            <div
              className={`image-card ${selected === 0 ? 'selected' : ''}`}
              onClick={() => handleSelect(0)}
            >
              {!img1Error ? (
                <img
                  src={question.image1}
                  alt={question.image1Label}
                  className="test-image"
                  onError={() => handleImageError(question.id, 1)}
                  loading="lazy"
                />
              ) : (
                <div className="image-placeholder">
                  <span>🖼️</span>
                  <p className="placeholder-text">{question.image1Label}</p>
                </div>
              )}
              <p className="image-label">{question.image1Label}</p>
            </div>

            <div
              className={`image-card ${selected === 1 ? 'selected' : ''}`}
              onClick={() => handleSelect(1)}
            >
              {!img2Error ? (
                <img
                  src={question.image2}
                  alt={question.image2Label}
                  className="test-image"
                  onError={() => handleImageError(question.id, 2)}
                  loading="lazy"
                />
              ) : (
                <div className="image-placeholder">
                  <span>🖼️</span>
                  <p className="placeholder-text">{question.image2Label}</p>
                </div>
              )}
              <p className="image-label">{question.image2Label}</p>
            </div>
          </div>

          <div className="progress-indicators">
            {Array.from({ length: totalQuestions }).map((_, idx) => (
              <div key={idx} className={`progress-dot ${getStatusColor(idx)}`} />
            ))}
          </div>

          <button className="stop-btn" onClick={handleStop}>
            <span className="stop-btn-shadow"></span>
            <span className="stop-btn-blink"></span>
            <span className="stop-btn-text">СТОП</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoundTest;